import { useState, useEffect, useCallback } from 'react';
import { isNumber, gte } from 'lodash';

const isWithin = (min, max, value) =>
  isNumber(min) &&
  isNumber(max) &&
  isNumber(value) &&
  gte(value, min) &&
  gte(max, value);

export const getApiErrorData = (errorData) => ({
  message:
    errorData?.error?.message ||
    'Oops, tuvimos un problema, intente nuevamente mas tarde',
  errorCode: errorData?.error?.error_code ?? null,
});

export const executeAsyncRequest = async ({
  values,
  request,
  onPrefetch,
  onSuccess,
  onError,
  onPostFetch,
  onFinally,
}) => {
  try {
    onPrefetch();
    const response = await request(values);
    const in200s = isWithin(200, 299, response.status);

    if (in200s) {
      onSuccess(response.data, response.headers);
    } else {
      onError({
        problem: response.statusText,
        errorData: getApiErrorData(response.data),
        status: response.status,
      });
    }
    onPostFetch(response);
  } catch (err) {
    const errorResponse = err?.response;

    onError({
      problem: errorResponse.statusText,
      errorData: getApiErrorData(errorResponse.data),
      status: errorResponse.status,
    });
  } finally {
    onFinally();
  }
};

// Returns a request to execute manually at some point, and the variables that
// will be updated when it does
export const useLazyRequest = ({
  request,
  withPostSuccess,
  withPostFailure,
  initialState = null,
  withPostFetch,
  transformResponse = (response) => response,
}) => {
  const [state, setState] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sendRequest = useCallback(
    (values) =>
      executeAsyncRequest({
        values,
        request,
        onPrefetch: () => {
          setLoading(true);
          setState(initialState);
          setError(null);
        },
        onSuccess: (data, headers) => {
          const transformedResponse = data
            ? transformResponse(data)
            : undefined;
          setState(transformedResponse);

          withPostSuccess?.(transformedResponse, headers);
        },
        onError: (errorInfo) => {
          setError(() => errorInfo);

          withPostFailure?.(errorInfo);
        },
        onPostFetch: (response) => {
          if (response.data) {
            withPostFetch?.(response.data);
          }
        },
        onFinally: () => {
          setLoading(false);
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialState, request, withPostFailure, withPostSuccess],
  );

  return [state, loading, error, sendRequest];
};

// Executes a request each time a dependency changes and returns the values and the refetch function
// in case you want to execute it again
export const useRequest = (
  {
    request,
    payload,
    withPostSuccess,
    withPostFailure,
    initialState = null,
    withPostFetch,
    transformResponse = (response) => response,
  },
  dependencies,
) => {
  const [state, loading, error, sendRequest] = useLazyRequest({
    request,
    withPostSuccess,
    withPostFailure,
    initialState,
    withPostFetch,
    transformResponse,
  });

  useEffect(
    () => {
      sendRequest(payload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    dependencies,
  );

  return [state, loading, error, sendRequest];
};
